import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { GraduationCap, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import GoogleLoginButton from '../components/GoogleLoginButton';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            toast.error('Por favor completa todos los campos');
            return;
        }

        setLoading(true);
        try {
            await login(email, password);
            toast.success('¡Bienvenido/a de vuelta!');
            navigate('/empezar');
        } catch (error) {
            console.error('Login error:', error);
            toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="mb-8 text-center">
                    <Link to="/" className="inline-flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-900">
                            <GraduationCap className="h-7 w-7 text-white" />
                        </div>
                        <div className="text-left leading-tight">
                            <span className="block font-heading text-lg font-bold text-slate-900">Portafolio Digital Docente</span>
                            <p className="text-xs text-slate-500 tracking-wide">Acontista Educa</p>
                        </div>
                    </Link>
                </div>

                <Card className="border-slate-200 shadow-lg">
                    <CardHeader className="space-y-1 pb-6">
                        <CardTitle className="font-heading text-2xl">Iniciar Sesión</CardTitle>
                        <CardDescription>
                            Accede a tu plataforma de estrategia pedagógica
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <GoogleLoginButton />
                        <div className="my-4 flex items-center gap-3">
                            <span className="flex-1 h-px bg-slate-200" />
                            <span className="text-[10px] uppercase tracking-widest text-slate-400">o continúa con email</span>
                            <span className="flex-1 h-px bg-slate-200" />
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email">Correo Electrónico</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="tu@correo.cl"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading}
                                    data-testid="login-email"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">Contraseña</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={loading}
                                        data-testid="login-password"
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>
                            <Button
                                type="submit"
                                className="w-full"
                                disabled={loading}
                                data-testid="login-submit"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Ingresando...
                                    </>
                                ) : (
                                    'Iniciar Sesión'
                                )}
                            </Button>
                        </form>
                        <div className="mt-6 text-center text-sm">
                            <span className="text-slate-500">¿No tienes cuenta? </span>
                            <Link to="/register" className="font-medium text-slate-900 hover:underline">
                                Regístrate
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
